import React, { useState, useEffect } from 'react';
import { Warehouse } from '@/api/entities';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const initialFormState = {
  name: '',
  address: {
    street: '',
    city: '',
    state: '',
    postal_code: '',
    country: ''
  },
  is_active: true,
};

export default function WarehouseForm({ isOpen, onClose, onSave, warehouse = null }) {
    const [formData, setFormData] = useState(initialFormState);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (isOpen) {
            if (warehouse) {
                // Handle both string and object address formats
                let address = initialFormState.address;
                if (warehouse.address) {
                    if (typeof warehouse.address === 'string') {
                        // If address is a string, try to parse it or use as street
                        address = {
                            street: warehouse.address,
                            city: '',
                            state: '',
                            postal_code: '',
                            country: ''
                        };
                    } else if (typeof warehouse.address === 'object') {
                        address = {
                            street: warehouse.address.street || '',
                            city: warehouse.address.city || '',
                            state: warehouse.address.state || '',
                            postal_code: warehouse.address.postal_code || '',
                            country: warehouse.address.country || ''
                        };
                    }
                }
                
                setFormData({
                    name: warehouse.name || '',
                    address: address,
                    is_active: warehouse.is_active ?? true,
                });
            } else {
                setFormData(initialFormState);
            }
        }
    }, [isOpen, warehouse]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            // Only include address if it has at least one field filled
            const submitData = {
                name: formData.name,
                is_active: formData.is_active
            };

            // Check if address has any content
            const hasAddressContent = Object.values(formData.address).some(value => value && value.trim() !== '');
            if (hasAddressContent) {
                submitData.address = formData.address;
            }

            if (warehouse) {
                await Warehouse.update(warehouse.id, submitData);
            } else {
                await Warehouse.create(submitData);
            }
            onSave();
        } catch (error) {
            console.error('Error saving warehouse:', error);
        }
        
        setIsSubmitting(false);
    };

    const updateAddress = (field, value) => {
        setFormData(prev => ({
            ...prev,
            address: {
                ...prev.address,
                [field]: value
            }
        }));
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-md bg-white" aria-describedby="warehouse-form-description">
                <DialogHeader>
                    <DialogTitle className="text-xl font-bold text-slate-900">
                        {warehouse ? 'Редактировать склад' : 'Новый склад'}
                    </DialogTitle>
                    <DialogDescription id="warehouse-form-description">
                        Заполните информацию о складе
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <Label htmlFor="name">Название склада *</Label>
                        <Input
                            id="name"
                            value={formData.name}
                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                            required
                            className="bg-white"
                        />
                    </div>

                    <div className="space-y-3">
                        <Label>Адрес</Label>
                        <div className="space-y-2">
                            <Input
                                placeholder="Улица"
                                value={formData.address.street}
                                onChange={(e) => updateAddress('street', e.target.value)}
                                className="bg-white"
                            />
                            <div className="grid grid-cols-2 gap-2">
                                <Input
                                    placeholder="Город"
                                    value={formData.address.city}
                                    onChange={(e) => updateAddress('city', e.target.value)}
                                    className="bg-white"
                                />
                                <Input
                                    placeholder="Область/Регион"
                                    value={formData.address.state}
                                    onChange={(e) => updateAddress('state', e.target.value)}
                                    className="bg-white"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <Input
                                    placeholder="Почтовый индекс"
                                    value={formData.address.postal_code}
                                    onChange={(e) => updateAddress('postal_code', e.target.value)}
                                    className="bg-white"
                                />
                                <Input
                                    placeholder="Страна"
                                    value={formData.address.country}
                                    onChange={(e) => updateAddress('country', e.target.value)}
                                    className="bg-white"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center space-x-2">
                        <Switch
                            id="is_active"
                            checked={formData.is_active}
                            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                        />
                        <Label htmlFor="is_active">Активный склад</Label>
                    </div>

                    <div className="flex justify-between items-center pt-4">
                        <Button type="button" variant="outline" onClick={onClose} className="bg-white">
                            Отмена
                        </Button>
                        <Button 
                            type="submit" 
                            disabled={isSubmitting || !formData.name}
                            className="bg-amber-600 hover:bg-amber-700 text-white"
                        >
                            {isSubmitting ? 'Сохранение...' : 'Сохранить склад'}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}